import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, ArrowLeft, Check, HelpCircle, Star, ShieldCheck, HeartPulse, Sparkles, CheckCircle2, Plus, X,
} from 'lucide-react';
import { HEALTH_INSURERS, generateHealthQuotes, insurers as homeInsurers } from '../mock';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import UI from '../lib/uiCatalog';
import { quotesApi, taxLinkApi, insurersApi } from '../lib/api';
import { useWizardAutoAuth } from '../lib/useWizardAutoAuth';
import InsurerCard from '../components/InsurerCard';
import QuoteFilters from '../components/QuoteFilters';
import SaveQuotePdf from '../components/SaveQuotePdf';
import Seo from '../components/Seo';

const initialData = {
  applicants: [{ id: 1, relation: 'primary', name: '', age: '', gender: '' }],
  // Contact
  ownerName: '', ownerEmail: '', ownerPhone: '', ownerNIE: '', ownerCountry: '',
  ownerPassword: '',
  startDate: '', currentInsurer: '', reason: 'first',
  // Health
  smoker: false, chronicConditions: false, medications: false, pregnancy: false,
  plannedSurgery: false, recentHospital: false, conditionsNotes: '',
  // Cover
  coverLevel: 'standard',
  noCopay: false, dental: true, maternity: false, mentalHealth: false,
  international: true, privateRoom: false, optical: false,
  chronicExtra: false,
};

export default function HealthInsurance() {
  const [current, setCurrent] = useState(0);
  const [data, setData] = useState(initialData);
  const [saving, setSaving] = useState(false);
  const [taxStatus, setTaxStatus] = useState(null);
  const [prefilling, setPrefilling] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [insurerProfiles, setInsurerProfiles] = useState({});
  const [filteredQuotes, setFilteredQuotes] = useState(null);
  const quoteListRef = useRef(null);
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const T = (k) => t(k, UI[k]);
  const navigate = useNavigate();

  const steps = [
    { key: 'applicants', label: T('hw.step_applicants') },
    { key: 'contact',    label: T('hw.step_contact') },
    { key: 'health',     label: T('hw.step_health') },
    { key: 'cover',      label: T('hw.step_cover') },
    { key: 'review',     label: T('hw.step_review') },
    { key: 'quote',      label: T('hw.step_quote') },
  ];

  useEffect(() => {
    if (!user) { setTaxStatus(null); return; }
    taxLinkApi.status().then(setTaxStatus).catch(() => setTaxStatus(null));
  }, [user]);

  const prefillFromTax = async () => {
    setPrefilling(true);
    try {
      const p = await taxLinkApi.prefill();
      setData((d) => ({
        ...d,
        ownerName: p.ownerName || d.ownerName,
        ownerEmail: p.ownerEmail || d.ownerEmail,
        ownerPhone: p.ownerPhone || d.ownerPhone,
        ownerNIE: p.ownerNIE || d.ownerNIE,
        ownerCountry: p.ownerCountry || d.ownerCountry,
        applicants: d.applicants.map((a, i) => i === 0 ? { ...a, name: a.name || p.ownerName || '' } : a),
      }));
      setPrefilled(true);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Could not prefill from EasySpanishTax');
    } finally {
      setPrefilling(false);
    }
  };

  const total = steps.length;
  const progress = ((current + 1) / total) * 100;
  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const auth = useWizardAutoAuth({
    email: data.ownerEmail,
    password: data.ownerPassword,
    name: data.ownerName,
    t, UI,
  });

  const next = async () => {
    const stepKey = steps[current]?.key;
    if (stepKey === 'contact') {
      const ok = await auth.advanceFromOwnerStep();
      if (!ok) return;
    }
    setCurrent((c) => Math.min(c + 1, total - 1));
  };
  const back = () => setCurrent((c) => Math.max(c - 1, 0));

  const quotes = useMemo(
    () => (current === total - 1 ? generateHealthQuotes(data) : []),
    [current, data, total],
  );

  useEffect(() => {
    if (current !== total - 1 || quotes.length === 0) return;
    const names = quotes.map((q) => q.insurer);
    insurersApi.lookup(names, lang).then(setInsurerProfiles).catch(() => setInsurerProfiles({}));
  }, [current, quotes, lang, total]);

  const saveToAccount = async () => {
    if (!user) {
      try { localStorage.setItem('esi_pending_quote', JSON.stringify({ data, quotes, product: 'health' })); } catch (_) { /* ignore */ }
      navigate('/login');
      return;
    }
    setSaving(true);
    try {
      await quotesApi.create({ data, quotes, product: 'health', status: 'draft' });
      navigate('/dashboard/quotes');
    } catch (err) {
      alert('Could not save quote: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#f7fafd] min-h-screen">
      <Seo pageKey="health-insurance" />
      <div className="container-x pt-10 pb-4">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
        >
          <ArrowLeft size={14} /> {T('wizard.back')}
        </button>
        <h1 className="mt-3 text-3xl sm:text-4xl font-bold text-[#0b1f3a] tracking-tight">{T('hw.page_title')}</h1>
        <p className="text-slate-600 mt-2">{T('hw.page_subtitle')}</p>

        {user && taxStatus?.linked && current === 0 && !prefilled && (
          <div className="mt-4 bg-white border border-[#0d3a8a]/30 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 shadow-sm">
            <span className="w-10 h-10 rounded-xl bg-[#eef4fb] text-[#0d3a8a] inline-flex items-center justify-center shrink-0">
              <Sparkles size={18} />
            </span>
            <div className="flex-1">
              <p className="font-semibold text-[#0b1f3a] text-sm">{T('wizard.tax_found_title')}</p>
              <p className="text-xs text-slate-600 mt-0.5">{T('wizard.tax_found_body')}</p>
            </div>
            <button
              onClick={prefillFromTax}
              disabled={prefilling}
              className="btn-primary !py-2 !px-4 text-sm disabled:opacity-60"
              data-testid="health-prefill-btn"
            >
              <Sparkles size={14} /> {prefilling ? T('wizard.prefilling') : T('wizard.prefill_btn')}
            </button>
          </div>
        )}

        {prefilled && (
          <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2 text-sm text-emerald-800">
            <CheckCircle2 size={16} /> {T('wizard.prefilled')}
          </div>
        )}
      </div>

      <div className="container-x pb-16">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 pt-6">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500 uppercase tracking-wider">
                {T('wizard.step_of').replace('{current}', current + 1).replace('{total}', total)}
              </span>
              <span className="text-xs text-slate-500">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="mt-2 h-2" />
            <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2">
              {steps.map((s, i) => (
                <div
                  key={s.key}
                  className={`progress-step ${i === current ? 'active' : i < current ? 'done' : ''}`}
                >
                  <span className={`step-dot ${i === current ? 'active' : i < current ? 'done' : ''}`}>
                    {i < current ? <Check size={14} /> : i + 1}
                  </span>
                  <span className="hidden sm:inline">{s.label}</span>
                  {i < steps.length - 1 && <span className="text-slate-300 hidden sm:inline">›</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6 pt-8">
            <div className="lg:col-span-2" data-testid="health-step-content">
              <HealthStep stepKey={steps[current].key} data={data} update={update} quotes={quotes} insurerProfiles={insurerProfiles} filteredQuotes={filteredQuotes} setFilteredQuotes={setFilteredQuotes} quoteListRef={quoteListRef} T={T} auth={auth} />
            </div>
            <aside className="space-y-4">
              <div className="help-box">
                <div className="flex items-start gap-2">
                  <HelpCircle size={18} className="mt-0.5" />
                  <div>
                    <p className="font-semibold">{T('wizard.help_title')}</p>
                    <p className="mt-1 text-slate-700">{T('wizard.help_body')}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-2 text-[#0d3a8a] font-semibold text-sm">
                  <ShieldCheck size={16} /> {T('wizard.data_safe_title')}
                </div>
                <p className="mt-2 text-xs text-slate-600 leading-relaxed">{T('wizard.data_safe_body')}</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-2 text-[#0d3a8a] font-semibold text-sm">
                  <HeartPulse size={16} /> {T('wizard.saved_title')}
                </div>
                <p className="mt-2 text-xs text-slate-600 leading-relaxed">{T('wizard.saved_body')}</p>
              </div>
            </aside>
          </div>

          <div className="border-t border-slate-100 p-5 flex items-center justify-between bg-slate-50">
            <button
              onClick={back}
              disabled={current === 0}
              className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="health-back-btn"
            >
              <ArrowLeft size={16} /> {T('wizard.back')}
            </button>
            {current < total - 1 ? (
              <button onClick={next} className="btn-primary" data-testid="health-next-btn">
                {current === total - 2 ? T('wizard.see_quote') : T('wizard.continue')} <ArrowRight size={16} />
              </button>
            ) : (
              <button onClick={saveToAccount} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="health-save-btn">
                {saving ? T('wizard.saving') : (user ? T('wizard.save_account') : T('wizard.save_login'))} <ArrowRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="field-label">{label}</Label>
      {children}
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 cursor-pointer">
      <span className="text-sm text-slate-700">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}

function HealthStep({ stepKey, data, update, quotes, insurerProfiles = {}, filteredQuotes, setFilteredQuotes, quoteListRef, T, auth }) {
  switch (stepKey) {
    case 'applicants': {
      const setApplicants = (fn) => update({ applicants: fn(data.applicants) });
      const addApplicant = () =>
        setApplicants((arr) => [...arr, { id: Date.now(), relation: 'child', name: '', age: '', gender: '' }]);
      const updateApplicant = (id, patch) =>
        setApplicants((arr) => arr.map((a) => (a.id === id ? { ...a, ...patch } : a)));
      const removeApplicant = (id) => setApplicants((arr) => arr.filter((a) => a.id !== id));
      return (
        <div className="space-y-5 fade-up">
          <div>
            <h2 className="text-xl font-semibold text-[#0b1f3a]">{T('hw.applicants_title')}</h2>
            <p className="text-slate-600 text-sm mt-1">{T('hw.applicants_help')}</p>
          </div>
          <div className="space-y-4">
            {data.applicants.map((a, i) => (
              <div key={a.id} className="border border-slate-200 rounded-xl p-4 bg-slate-50/50 relative">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-[#0b1f3a]">
                    #{i + 1} · {a.relation === 'primary' ? T('hw.rel_primary') : a.relation === 'spouse' ? T('hw.rel_spouse') : a.relation === 'child' ? T('hw.rel_child') : T('hw.rel_other')}
                  </p>
                  {data.applicants.length > 1 && a.relation !== 'primary' && (
                    <button
                      type="button"
                      onClick={() => removeApplicant(a.id)}
                      className="text-slate-400 hover:text-rose-600 inline-flex items-center gap-1 text-xs"
                    >
                      <X size={12} /> {T('hw.remove_applicant')}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label={T('hw.applicant_relation')}>
                    <Select value={a.relation} onValueChange={(v) => updateApplicant(a.id, { relation: v })} disabled={i === 0}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="primary">{T('hw.rel_primary')}</SelectItem>
                        <SelectItem value="spouse">{T('hw.rel_spouse')}</SelectItem>
                        <SelectItem value="child">{T('hw.rel_child')}</SelectItem>
                        <SelectItem value="other">{T('hw.rel_other')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label={T('hw.applicant_name')}>
                    <Input value={a.name} onChange={(e) => updateApplicant(a.id, { name: e.target.value })} />
                  </Field>
                  <Field label={T('hw.applicant_age')}>
                    <Input type="number" min="0" max="99" value={a.age} onChange={(e) => updateApplicant(a.id, { age: e.target.value })} placeholder="42" />
                  </Field>
                  <Field label={T('hw.applicant_gender')}>
                    <Select value={a.gender} onValueChange={(v) => updateApplicant(a.id, { gender: v })}>
                      <SelectTrigger><SelectValue placeholder={T('hw.gender_ph')} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">{T('hw.gender_male')}</SelectItem>
                        <SelectItem value="female">{T('hw.gender_female')}</SelectItem>
                        <SelectItem value="other">{T('hw.gender_other')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addApplicant}
            className="btn-outline !py-2 !px-4 text-sm"
            data-testid="health-add-applicant"
          >
            <Plus size={14} /> {T('hw.add_applicant')}
          </button>
        </div>
      );
    }
    case 'contact':
      return (
        <div className="space-y-5 fade-up">
          <div>
            <h2 className="text-xl font-semibold text-[#0b1f3a]">{T('hw.contact_title')}</h2>
            <p className="text-slate-600 text-sm mt-1">{T('hw.contact_help')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={T('hw.field_name')}><Input value={data.ownerName} onChange={(e) => update({ ownerName: e.target.value })} /></Field>
            <Field label={T('hw.field_email')}>
              <Input
                type="email"
                value={data.ownerEmail}
                onChange={(e) => update({ ownerEmail: e.target.value })}
                data-testid="wizard-owner-email"
              />
              {auth.emailStatus.checking && (
                <p className="text-xs text-slate-500 mt-1">{T('wf.email_checking')}</p>
              )}
              {!auth.isLoggedIn && auth.emailStatus.exists === true && (
                <p className="text-xs text-emerald-700 mt-1" data-testid="wizard-email-existing">
                  {T('wf.email_existing')}
                </p>
              )}
            </Field>
            <Field label={T('wf.password')}>
              <Input
                type="password"
                value={auth.passwordDisabled ? '' : (data.ownerPassword || '')}
                onChange={(e) => update({ ownerPassword: e.target.value })}
                disabled={auth.passwordDisabled}
                placeholder={auth.passwordPlaceholder}
                minLength={6}
                autoComplete="new-password"
                data-testid="wizard-owner-password"
              />
              {!auth.isLoggedIn && auth.emailStatus.exists === false && (
                <p className="text-xs text-slate-500 mt-1" data-testid="wizard-password-help">
                  {T('wf.password_help')}
                </p>
              )}
              {auth.authError && (
                <p className="text-xs text-rose-600 mt-1" data-testid="wizard-auth-error">
                  {auth.authError}
                </p>
              )}
            </Field>
            <Field label={T('hw.field_phone')}><Input value={data.ownerPhone} onChange={(e) => update({ ownerPhone: e.target.value })} /></Field>
            <Field label={T('hw.field_nie')}><Input value={data.ownerNIE} onChange={(e) => update({ ownerNIE: e.target.value })} /></Field>
            <Field label={T('hw.field_country')}><Input value={data.ownerCountry} onChange={(e) => update({ ownerCountry: e.target.value })} /></Field>
            <Field label={T('hw.field_start')}><Input type="date" value={data.startDate} onChange={(e) => update({ startDate: e.target.value })} /></Field>
            <Field label={T('hw.field_current_insurer')}>
              <Select value={data.currentInsurer} onValueChange={(v) => update({ currentInsurer: v })}>
                <SelectTrigger><SelectValue placeholder={T('hw.current_insurer_ph')} /></SelectTrigger>
                <SelectContent>
                  {HEALTH_INSURERS.map((i) => <SelectItem key={i.name} value={i.name}>{i.name}</SelectItem>)}
                  {homeInsurers.filter((i) => !HEALTH_INSURERS.find((h) => h.name === i)).map((i) => (
                    <SelectItem key={i} value={i}>{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={T('hw.field_reason')}>
              <Select value={data.reason} onValueChange={(v) => update({ reason: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="first">{T('hw.reason_first')}</SelectItem>
                  <SelectItem value="visa">{T('hw.reason_visa')}</SelectItem>
                  <SelectItem value="switch">{T('hw.reason_switch')}</SelectItem>
                  <SelectItem value="price">{T('hw.reason_price')}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </div>
      );
    case 'health':
      return (
        <div className="space-y-5 fade-up">
          <div>
            <h2 className="text-xl font-semibold text-[#0b1f3a]">{T('hw.health_title')}</h2>
            <p className="text-slate-600 text-sm mt-1">{T('hw.health_help')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Toggle label={T('hw.smoker')}         checked={data.smoker}             onChange={(v) => update({ smoker: v })} />
            <Toggle label={T('hw.chronic')}        checked={data.chronicConditions}  onChange={(v) => update({ chronicConditions: v })} />
            <Toggle label={T('hw.medications')}    checked={data.medications}        onChange={(v) => update({ medications: v })} />
            <Toggle label={T('hw.pregnancy')}      checked={data.pregnancy}          onChange={(v) => update({ pregnancy: v })} />
            <Toggle label={T('hw.surgery')}        checked={data.plannedSurgery}     onChange={(v) => update({ plannedSurgery: v })} />
            <Toggle label={T('hw.hospital_last3')} checked={data.recentHospital}     onChange={(v) => update({ recentHospital: v })} />
          </div>
          <Field label={T('hw.conditions_notes')}>
            <Textarea rows={3} value={data.conditionsNotes} onChange={(e) => update({ conditionsNotes: e.target.value })} placeholder={T('hw.conditions_ph')} />
          </Field>
        </div>
      );
    case 'cover':
      return (
        <div className="space-y-5 fade-up">
          <div>
            <h2 className="text-xl font-semibold text-[#0b1f3a]">{T('hw.cover_title')}</h2>
            <p className="text-slate-600 text-sm mt-1">{T('hw.cover_help')}</p>
          </div>
          <Field label={T('hw.cover_level')}>
            <Select value={data.coverLevel} onValueChange={(v) => update({ coverLevel: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="basic">{T('hw.level_basic')}</SelectItem>
                <SelectItem value="standard">{T('hw.level_standard')}</SelectItem>
                <SelectItem value="premium">{T('hw.level_premium')}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div>
            <p className="field-label mb-2">{T('hw.extras')}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Toggle label={T('hw.no_copay')}      checked={data.noCopay}       onChange={(v) => update({ noCopay: v })} />
              <Toggle label={T('hw.dental')}        checked={data.dental}        onChange={(v) => update({ dental: v })} />
              <Toggle label={T('hw.maternity')}     checked={data.maternity}     onChange={(v) => update({ maternity: v })} />
              <Toggle label={T('hw.mental_health')} checked={data.mentalHealth}  onChange={(v) => update({ mentalHealth: v })} />
              <Toggle label={T('hw.international')} checked={data.international} onChange={(v) => update({ international: v })} />
              <Toggle label={T('hw.private_room')}  checked={data.privateRoom}   onChange={(v) => update({ privateRoom: v })} />
              <Toggle label={T('hw.optical')}       checked={data.optical}       onChange={(v) => update({ optical: v })} />
              <Toggle label={T('hw.chronic_extra')} checked={data.chronicExtra} onChange={(v) => update({ chronicExtra: v })} />
            </div>
          </div>
        </div>
      );
    case 'review':
      return (
        <div className="space-y-5 fade-up">
          <h2 className="text-xl font-semibold text-[#0b1f3a]">{T('hw.review_title')}</h2>
          <p className="text-slate-600 text-sm">{T('hw.review_sub')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <ReviewCard title={T('hw.rev_applicants')} items={data.applicants.map((a, i) => [
              `#${i + 1} ${a.relation === 'primary' ? T('hw.rel_primary') : a.relation === 'spouse' ? T('hw.rel_spouse') : a.relation === 'child' ? T('hw.rel_child') : T('hw.rel_other')}`,
              `${a.name || '—'} · ${a.age || '—'}`,
            ])} />
            <ReviewCard title={T('hw.rev_contact')} items={[
              [T('hw.field_name'), data.ownerName || '—'],
              [T('hw.field_email'), data.ownerEmail || '—'],
              [T('hw.field_phone'), data.ownerPhone || '—'],
              [T('hw.field_country'), data.ownerCountry || '—'],
              [T('hw.field_start'), data.startDate || '—'],
            ]} />
            <ReviewCard title={T('hw.rev_health')} items={[
              [T('hw.smoker'), data.smoker ? 'Yes' : 'No'],
              [T('hw.chronic'), data.chronicConditions ? 'Yes' : 'No'],
              [T('hw.medications'), data.medications ? 'Yes' : 'No'],
              [T('hw.hospital_last3'), data.recentHospital ? 'Yes' : 'No'],
            ]} />
            <ReviewCard title={T('hw.rev_cover')} items={[
              [T('hw.cover_level'),
                data.coverLevel === 'basic' ? T('hw.level_basic') :
                data.coverLevel === 'premium' ? T('hw.level_premium') : T('hw.level_standard')],
              [T('hw.no_copay'), data.noCopay ? 'Yes' : 'No'],
              [T('hw.dental'), data.dental ? 'Yes' : 'No'],
              [T('hw.maternity'), data.maternity ? 'Yes' : 'No'],
              [T('hw.international'), data.international ? 'Yes' : 'No'],
            ]} />
          </div>
        </div>
      );
    case 'quote': {
      const shownQuotes = filteredQuotes ?? quotes;
      return (
        <div className="space-y-5 fade-up">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-xl font-semibold text-[#0b1f3a]">{T('hw.quotes_title')}</h2>
              <p className="text-slate-600 text-sm mt-1">{T('hw.quotes_sub')}</p>
            </div>
            <SaveQuotePdf targetRef={quoteListRef} filename="health-insurance-quote.pdf" />
          </div>
          <QuoteFilters
            quotes={quotes}
            insurerProfiles={insurerProfiles}
            product="health"
            onFiltered={setFilteredQuotes}
          />
          <div ref={quoteListRef} className="space-y-3">
            {shownQuotes.map((q, i) => (
              <InsurerCard
                key={q.insurer}
                quote={q}
                profile={insurerProfiles[q.insurer] || null}
                isBest={i === 0}
              />
            ))}
            {shownQuotes.length === 0 && (
              <div className="border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500 text-sm">
                {T('filters.none_match') || 'No insurer matches all selected filters.'}
              </div>
            )}
          </div>
        </div>
      );
    }
    default:
      return null;
  }
}

function ReviewCard({ title, items }) {
  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
      <p className="font-semibold text-[#0b1f3a] text-sm mb-2">{title}</p>
      <ul className="space-y-1">
        {items.map(([k, v]) => (
          <li key={k} className="flex justify-between text-slate-600 gap-3">
            <span className="truncate">{k}</span>
            <span className="text-slate-800 text-right">{v}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
